/**
 * Participant-safe status language (Appendix A), the loss and sponsor-sample copy (§2.4, §4.2 E/F, §8) and
 * the template renderer. All participant-facing status copy lives here, never inline in an app (AGENTS.md).
 * Keys are the internal conditions; values carry template variables in `{braces}` and the required next action.
 */
import { findProhibitedCopy } from './copy-forbidden.js';
const status = (text, nextAction, variables = []) => ({ text, nextAction, variables });
/** Appendix A → Participant-safe status language, keyed by internal condition. */
export const PARTICIPANT_STATUS_COPY = {
    payment_pending: status('Your payment is still being confirmed. Your entry is not in the draw yet.', 'Check status; contact support after the displayed wait.'),
    payment_succeeded_entry_failed: status('We received your payment but could not confirm your entry. We are resolving this now.', 'Automatic support case; complete the entry or refund — never ask for another payment.'),
    payment_failed: status('Your payment was not completed and no entry was created. You can try again or use another method.', 'Retry with the same intent or an alternate method.'),
    payment_expired: status('This checkout can no longer be used. No entry was created.', 'Restart if the lottery remains open.'),
    payment_refunded: status('A refund of {amount} has been recorded. {entryEffect}', 'Show refund amount, status and resulting entry rights.', ['amount', 'entryEffect']),
    reconciliation_exception: status('We are checking this payment with our payment service. Your entry is on hold until it is confirmed.', 'Support case and next update time, never a false success.'),
    late_payment_excluded: status('Your payment was confirmed after entries closed, so it could not be included. A refund has been started.', 'Refund status and support route.'),
    verification_pending: status('Your claim is complete and waiting for review.', 'Show the expected review window.'),
    more_information: status('We need a clearer image of {document}. Your other information is saved.', 'Replace only the requested item; show the resubmission deadline.', ['document']),
    variant_hold_expired: status('The reservation time ended, so that option may no longer be available. Your win is still valid.', 'Show currently available choices.'),
    variant_hold_extended: status('We extended your reservation while we finish checking your details.', 'Show the new reservation end time.'),
    not_yet_messaged: status('Message us on {platform} to arrange your prize — tap to open a ready-made message.', 'One-tap hand-off to the pre-filled message.', ['platform']),
    provider_outage: status('The {service} service is temporarily unavailable. We will keep your information and update this page.', 'Retry/status plus support; extend platform-caused deadlines.', ['service']),
    fulfillment_exception: status('Your prize is delayed at {stage}. {owner} is investigating.', 'Named next update time and escalation route.', ['stage', 'owner']),
    fulfillment_in_progress: status('Your prize is being prepared by {owner}. Next update by {nextUpdateAt}.', 'Show owner, last update and next milestone.', ['owner', 'nextUpdateAt']),
    claim_rejected: status('Your claim was not approved because {safeReason}. You may request a review.', 'Appeal instructions and deadline.', ['safeReason']),
    claim_approved: status('Your claim is approved. {owner} will now arrange your prize.', 'Show the fulfillment timeline.', ['owner']),
    claim_window_closing: status('Your claim must be submitted by {deadline} ({timezone}).', 'Deadline in the lottery timezone and a direct link to continue.', ['deadline', 'timezone']),
    claim_expired: status('The claim window for this prize has ended. The published unclaimed-prize rule now applies.', 'Support route and the published policy.'),
    lottery_paused: status('Entries are paused while the organizer resolves an issue. Existing entries are kept.', 'Explain, remove the entry action, link to updates.'),
    lottery_cancelled: status('This lottery was cancelled. Paid entries are being refunded in full.', 'Link to refund status and aggregate refund progress.'),
    results_pending: status('The draw is scheduled for {drawAt} ({timezone}). Your entry {entryId} is in the record.', 'Show the draw time and the receipt.', ['drawAt', 'timezone', 'entryId']),
    stale_data: status('Showing saved information from {asOf}. Live updates will resume automatically.', 'Cached read-only page with a stale-data label.', ['asOf']),
    offline: status('You are offline. Nothing you entered has been lost.', 'Retry when the connection returns.'),
};
export const PARTICIPANT_STATUS_KEYS = Object.keys(PARTICIPANT_STATUS_COPY);
/** §2.4 / §4.2 F loss copy: warm, honest, no near-miss, no suggestion that a loss improves later odds. */
export const LOSS_COPY = {
    headline: 'Not this time — your entry is in the draw record.',
    /** Re-entry is one quiet action with its real cost and closing time; nothing implies a better chance. */
    reentry: 'Enter again for {cost} — entries close {closesAt}.',
    reentryFree: 'Enter again — entries close {closesAt}.',
    variables: ['cost', 'closesAt'],
};
/** §8 / §4.2 E sponsor-sample copy. Must not imply the main-prize odds changed. */
export const SPONSOR_SAMPLE_COPY = {
    template: "You didn't win the {mainPrize} this time — but you've won {sponsorProduct} as part of the published sponsor sample.",
    variables: ['mainPrize', 'sponsorProduct'],
};
/** Sampling-rule copy variables organizers may use (§8 "Reveal copy"). */
export const SAMPLING_COPY_VARIABLES = ['sponsor', 'product', 'mainPrize'];
/** §10.2 participant display per payment state. */
export const PAYMENT_STATE_COPY = {
    INITIALIZED: 'Checkout not complete',
    PENDING: 'Payment pending — check status',
    SUCCEEDED: 'Payment and entry confirmed',
    FAILED: 'Payment not completed',
    EXPIRED: 'Checkout expired — restart if the lottery is open',
    CANCELLED: 'Checkout cancelled — restart if the lottery is open',
    REFUNDED: 'Refunded',
    PARTIALLY_REFUNDED: 'Partially refunded',
    RECONCILIATION_EXCEPTION: 'Being checked with the payment service',
};
/** §12.3: button taps are never shown as delivered messages. */
export const WINNER_CONTACT_COPY = {
    NOT_MESSAGED: 'Message us to arrange your prize',
    BUTTON_TAPPED: 'Message us to arrange your prize',
    MESSAGED: 'We have your message — an admin is in touch',
    FOLLOWED_UP: 'Follow-up recorded',
};
/** Winner hand-off pre-filled message (§12.2): win ID, prize and signed link; never contact details. */
export const WIN_MESSAGE_TEMPLATE = {
    template: 'Hi {organizer}! I won {prize} in {lottery}. My win ID is {winId}. Details: {winLink}',
    variables: ['organizer', 'prize', 'lottery', 'winId', 'winLink'],
};
const PLACEHOLDER = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;
/** Replace `{variable}` placeholders. Missing variables are left in place so tests catch them. */
export const renderTemplate = (template, vars = {}) => template.replace(PLACEHOLDER, (whole, name) => Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole);
/** Variables referenced by a template, in order of first appearance. */
export const templateVariables = (template) => {
    const out = [];
    for (const m of template.matchAll(PLACEHOLDER)) {
        const name = m[1] ?? '';
        if (name && !out.includes(name))
            out.push(name);
    }
    return out;
};
/** Render a participant status by key. */
export const renderStatusCopy = (key, vars = {}) => renderTemplate(PARTICIPANT_STATUS_COPY[key].text, vars);
/** Render the sponsor-sample reveal line. */
export const renderSponsorSampleCopy = (vars) => renderTemplate(SPONSOR_SAMPLE_COPY.template, vars);
/**
 * Validate organizer-authored sampling copy (§8): only approved variables, no prohibited patterns (§13.1).
 * Returns a list of problems; empty means acceptable.
 */
export const validateSamplingCopy = (template) => {
    const problems = [];
    for (const v of templateVariables(template)) {
        if (!SAMPLING_COPY_VARIABLES.includes(v))
            problems.push(`unknown variable {${v}}`);
    }
    const bad = findProhibitedCopy(template);
    if (bad)
        problems.push(`prohibited pattern (${bad.name}): "${bad.match}"`);
    return problems;
};
//# sourceMappingURL=copy.js.map